import { inBounds, parsePosKey, posKey } from "./grid";
import type { BoardState, MatchGroup, MatchShape, Position, SpecialKind } from "./types";

interface Primitive {
  id: number;
  cells: Position[];
  scoreLength: number;
  specialLength: number;
}

function isBombSpecial(special: SpecialKind): boolean {
  return special === "BOMB_3" || special === "BOMB_4" || special === "BOMB_5";
}

function isPassiveMatchExcludedSpecial(special: SpecialKind): boolean {
  return isBombSpecial(special) || special === "WIPE";
}

class Dsu {
  private parent: number[];

  constructor(size: number) {
    this.parent = Array.from({ length: size }, (_, i) => i);
  }

  find(x: number): number {
    if (this.parent[x] !== x) {
      this.parent[x] = this.find(this.parent[x]);
    }
    return this.parent[x];
  }

  union(a: number, b: number): void {
    const pa = this.find(a);
    const pb = this.find(b);
    if (pa !== pb) {
      this.parent[pb] = pa;
    }
  }
}

function collectLineRuns(board: BoardState): Primitive[] {
  const runs: Primitive[] = [];
  let idCounter = 0;

  for (let row = 0; row < board.size; row += 1) {
    let col = 0;
    while (col < board.size) {
      const cell = board.cells[row][col];
      if (cell.kind !== "TILE" || cell.tile == null || isPassiveMatchExcludedSpecial(cell.tile.special)) {
        col += 1;
        continue;
      }
      const color = cell.tile.color;
      const start = col;
      while (col < board.size) {
        const c = board.cells[row][col];
        if (
          c.kind !== "TILE"
          || c.tile == null
          || isPassiveMatchExcludedSpecial(c.tile.special)
          || c.tile.color !== color
        ) {
          break;
        }
        col += 1;
      }
      const len = col - start;
      if (len >= 3) {
        const cells: Position[] = [];
        for (let c = start; c < col; c += 1) {
          cells.push({ row, col: c });
        }
        runs.push({ id: idCounter, cells, scoreLength: len, specialLength: len });
        idCounter += 1;
      }
    }
  }

  for (let col = 0; col < board.size; col += 1) {
    let row = 0;
    while (row < board.size) {
      const cell = board.cells[row][col];
      if (cell.kind !== "TILE" || cell.tile == null || isPassiveMatchExcludedSpecial(cell.tile.special)) {
        row += 1;
        continue;
      }
      const color = cell.tile.color;
      const start = row;
      while (row < board.size) {
        const c = board.cells[row][col];
        if (
          c.kind !== "TILE"
          || c.tile == null
          || isPassiveMatchExcludedSpecial(c.tile.special)
          || c.tile.color !== color
        ) {
          break;
        }
        row += 1;
      }
      const len = row - start;
      if (len >= 3) {
        const cells: Position[] = [];
        for (let r = start; r < row; r += 1) {
          cells.push({ row: r, col });
        }
        runs.push({ id: idCounter, cells, scoreLength: len, specialLength: len });
        idCounter += 1;
      }
    }
  }

  return runs;
}

function collectSquareMatches(board: BoardState, startId: number): Primitive[] {
  const matches: Primitive[] = [];
  let idCounter = startId;
  for (let row = 0; row < board.size - 1; row += 1) {
    for (let col = 0; col < board.size - 1; col += 1) {
      const a = board.cells[row][col];
      const b = board.cells[row][col + 1];
      const c = board.cells[row + 1][col];
      const d = board.cells[row + 1][col + 1];
      if (
        a.kind !== "TILE" || a.tile == null ||
        b.kind !== "TILE" || b.tile == null ||
        c.kind !== "TILE" || c.tile == null ||
        d.kind !== "TILE" || d.tile == null
      ) {
        continue;
      }
      if (
        isPassiveMatchExcludedSpecial(a.tile.special)
        || isPassiveMatchExcludedSpecial(b.tile.special)
        || isPassiveMatchExcludedSpecial(c.tile.special)
        || isPassiveMatchExcludedSpecial(d.tile.special)
      ) {
        continue;
      }
      const color = a.tile.color;
      if (b.tile.color !== color || c.tile.color !== color || d.tile.color !== color) {
        continue;
      }
      matches.push({
        id: idCounter,
        cells: [
          { row, col },
          { row, col: col + 1 },
          { row: row + 1, col },
          { row: row + 1, col: col + 1 }
        ],
        scoreLength: 4,
        // Keep square matches out of run-length special spawning.
        specialLength: 3
      });
      idCounter += 1;
    }
  }
  return matches;
}

function cellAdjacencyDegrees(cells: Position[]): number[] {
  const set = new Set(cells.map((cell) => posKey(cell.row, cell.col)));
  const degrees: number[] = [];
  for (const cell of cells) {
    let degree = 0;
    if (set.has(posKey(cell.row - 1, cell.col))) degree += 1;
    if (set.has(posKey(cell.row + 1, cell.col))) degree += 1;
    if (set.has(posKey(cell.row, cell.col - 1))) degree += 1;
    if (set.has(posKey(cell.row, cell.col + 1))) degree += 1;
    degrees.push(degree);
  }
  return degrees.sort((a, b) => a - b);
}

function isSquareShape(cells: Position[]): boolean {
  if (cells.length !== 4) return false;
  const rows = [...new Set(cells.map((cell) => cell.row))].sort((a, b) => a - b);
  const cols = [...new Set(cells.map((cell) => cell.col))].sort((a, b) => a - b);
  if (rows.length !== 2 || cols.length !== 2) {
    return false;
  }
  const expected = new Set([
    posKey(rows[0], cols[0]),
    posKey(rows[0], cols[1]),
    posKey(rows[1], cols[0]),
    posKey(rows[1], cols[1])
  ]);
  return cells.every((cell) => expected.has(posKey(cell.row, cell.col)));
}

function isStraightLineShape(cells: Position[]): boolean {
  const rowCount = new Set(cells.map((cell) => cell.row)).size;
  const colCount = new Set(cells.map((cell) => cell.col)).size;
  return rowCount === 1 || colCount === 1;
}

function classifyShape(cells: Position[], hasIntersection: boolean): MatchShape {
  if (isSquareShape(cells)) {
    return "SQUARE";
  }

  if (cells.length === 5) {
    if (isStraightLineShape(cells)) {
      return "LINE";
    }
    const signature = cellAdjacencyDegrees(cells).join(",");
    if (signature === "1,1,1,1,4" || signature === "1,1,1,2,3") {
      return "T";
    }
    if (signature === "1,1,2,2,2") {
      return "L";
    }
  }

  if (hasIntersection) {
    return "COMPOSITE";
  }
  return "LINE";
}

function groupCenter(cells: Position[]): Position {
  const row = cells.reduce((acc, cell) => acc + cell.row, 0) / cells.length;
  const col = cells.reduce((acc, cell) => acc + cell.col, 0) / cells.length;
  return {
    row: Math.round(row),
    col: Math.round(col)
  };
}

export function findMatchGroups(board: BoardState): MatchGroup[] {
  const lineRuns = collectLineRuns(board);
  const squareRuns = collectSquareMatches(board, lineRuns.length);
  const primitives = [...lineRuns, ...squareRuns];
  if (primitives.length === 0) {
    return [];
  }

  const dsu = new Dsu(primitives.length);
  const byCell = new Map<string, number[]>();
  for (let i = 0; i < primitives.length; i += 1) {
    for (const cell of primitives[i].cells) {
      const key = posKey(cell.row, cell.col);
      const list = byCell.get(key) ?? [];
      list.push(i);
      byCell.set(key, list);
    }
  }

  for (const list of byCell.values()) {
    if (list.length >= 2) {
      for (let i = 1; i < list.length; i += 1) {
        dsu.union(list[0], list[i]);
      }
    }
  }

  const grouped = new Map<
    number,
    {
      cells: Set<string>;
      maxRunLength: number;
      maxSpecialRunLength: number;
      hasIntersection: boolean;
    }
  >();

  for (let i = 0; i < primitives.length; i += 1) {
    const root = dsu.find(i);
    const target = grouped.get(root) ?? {
      cells: new Set<string>(),
      maxRunLength: 0,
      maxSpecialRunLength: 0,
      hasIntersection: false
    };
    target.maxRunLength = Math.max(target.maxRunLength, primitives[i].scoreLength);
    target.maxSpecialRunLength = Math.max(target.maxSpecialRunLength, primitives[i].specialLength);
    for (const cell of primitives[i].cells) {
      target.cells.add(posKey(cell.row, cell.col));
      const joined = byCell.get(posKey(cell.row, cell.col));
      if (joined != null && joined.length > 1) {
        target.hasIntersection = true;
      }
    }
    grouped.set(root, target);
  }

  return Array.from(grouped.values()).map((group) => {
    const cells = Array.from(group.cells).map(parsePosKey);
    return {
      cells,
      maxRunLength: group.maxRunLength,
      maxSpecialRunLength: group.maxSpecialRunLength,
      hasIntersection: group.hasIntersection,
      shape: classifyShape(cells, group.hasIntersection),
      center: groupCenter(cells)
    };
  });
}

export function hasAnyMatch(board: BoardState): boolean {
  return findMatchGroups(board).length > 0;
}

export function createsMatchAfterSwap(
  board: BoardState,
  a: Position,
  b: Position
): boolean {
  if (!inBounds(board.size, a.row, a.col) || !inBounds(board.size, b.row, b.col)) {
    return false;
  }
  const tmp = board.cells[a.row][a.col];
  board.cells[a.row][a.col] = board.cells[b.row][b.col];
  board.cells[b.row][b.col] = tmp;
  const result = hasAnyMatch(board);
  board.cells[b.row][b.col] = board.cells[a.row][a.col];
  board.cells[a.row][a.col] = tmp;
  return result;
}
